import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Button, TextField, IconButton,
    Chip, Tooltip, InputAdornment, Checkbox, Menu, MenuItem, ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    // Removed 3 Nov 2026 (N20 pt 4): ArrowBack as ArrowBackIcon, the back arrow's icon.
    FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { skillService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import CategoryFormModal from './CategoryFormModal';
import BulkDeleteModal from '../User/BulkDeleteModal';
import { formatDate } from '../../utils/dateFormatter';
import EmptyState from '../common/EmptyState';
import PageTabs from '../shared/PageTabs';
import { SKILLS_TABS } from '../shared/tabConfigs';

// The Categories tab of the Skills area, at /skills/categories (N20 pt 4). Removed 3 Nov 2026 (N20 pt 4): the onBack
// prop; the Skills page no longer opens this list in its own place.
const CategoriesList = () => {
    const { showToast } = useToast();
    const [categories, setCategories] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCategories, setTotalCategories] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [permissions, setPermissions] = useState({
        canEdit: false,
        canDelete: false,
        canCreate: false
    });

    // Modal states
    const [categoryFormModalOpen, setCategoryFormModalOpen] = useState(false);
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // Menu states
    const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
    const isActionsMenuOpen = Boolean(actionsMenuAnchorEl);

    useEffect(() => {
        fetchCategories();
    }, [page, rowsPerPage, searchTerm]);

    useEffect(() => {
        setSelectedCategories([]);
    }, [page, rowsPerPage, searchTerm]);

    const fetchCategories = async () => {
        try {
            const response = await skillService.getCategoriesList(page + 1, rowsPerPage, searchTerm);
            if (response.data.success) {
                setCategories(response.data.categories);
                setTotalCategories(response.data.totalCategories);
                setPermissions(response.data.permissions);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            showToast('Error fetching categories', 'error');
            setCategories([]);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleSelectCategory = (categoryId) => {
        setSelectedCategories(prev => {
            if (prev.includes(categoryId)) {
                return prev.filter(id => id !== categoryId);
            } else {
                return [...prev, categoryId];
            }
        });
    };

    const handleSelectAllCategories = (event) => {
        if (event.target.checked && categories) {
            const currentPageCategoryIds = categories.map(category => category.id);
            setSelectedCategories(currentPageCategoryIds);
        } else {
            setSelectedCategories([]);
        }
    };

    const isAllSelected = categories && categories.length > 0 && selectedCategories.length === categories.length;

    // Menu handlers
    const handleActionsMenuClick = (event) => {
        setActionsMenuAnchorEl(event.currentTarget);
    };

    const handleActionsMenuClose = () => {
        setActionsMenuAnchorEl(null);
    };

    // Action handlers
    const handleCreateCategory = () => {
        setSelectedCategory(null);
        setCategoryFormModalOpen(true);
    };

    const handleEditCategory = (category) => {
        setSelectedCategory(category);
        setCategoryFormModalOpen(true);
    };

    const handleBulkDelete = () => {
        handleActionsMenuClose();
        setBulkDeleteModalOpen(true);
    };

    const handleCategoryFormSuccess = () => {
        fetchCategories();
    };

    const handleExportCategories = async () => {
        try {
            const response = await skillService.exportCategories(searchTerm);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'categories_export.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showToast('Categories exported successfully', 'success');
        } catch (error) {
            console.error('Error exporting categories:', error);
            showToast('Error exporting categories', 'error');
        }
    };

    const handleExportSelectedCategories = async () => {
        if (selectedCategories.length === 0) {
            showToast('Please select categories to export', 'warning');
            return;
        }

        try {
            const response = await skillService.exportSelectedCategories(selectedCategories);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'selected_categories_export.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showToast('Selected categories exported successfully', 'success');
            handleActionsMenuClose();
        } catch (error) {
            console.error('Error exporting selected categories:', error);
            showToast('Error exporting selected categories', 'error');
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <PageTabs tabs={SKILLS_TABS} />
            {/* Removed 3 Nov 2026 (N20 pt 4): the back arrow before the title; the Skills tab goes back. */}
            <Typography variant="h4" gutterBottom className='page-title'>
                Skill Categories
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Categories"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Box display="flex">
                    {selectedCategories.length > 0 && (
                        <Button
                            variant="outlined"
                            onClick={handleActionsMenuClick}
                            aria-controls={isActionsMenuOpen ? 'actions-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={isActionsMenuOpen ? 'true' : undefined}
                            sx={{ mr: 1 }}
                        >
                            Actions
                        </Button>
                    )}

                    {permissions.canCreate && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreateCategory}
                        >
                            Add Category
                        </Button>

                    )}
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportCategories}
                        sx={{ ml: 1 }}
                    >
                        Export All
                    </Button>
                </Box>
                <Menu
                    id="actions-menu"
                    anchorEl={actionsMenuAnchorEl}
                    open={isActionsMenuOpen}
                    onClose={handleActionsMenuClose}
                    MenuListProps={{
                        'aria-labelledby': 'actions-button',
                    }}
                >
                    <MenuItem onClick={handleExportSelectedCategories}>
                        <ListItemIcon>
                            <FileDownloadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Export Selected</ListItemText>
                    </MenuItem>
                    {permissions.canDelete && (
                        <MenuItem onClick={handleBulkDelete}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Delete Categories</ListItemText>
                        </MenuItem>
                    )}
                </Menu>
            </Box>

            {selectedCategories.length > 0 && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="white">
                        {selectedCategories.length} categor{selectedCategories.length > 1 ? 'ies' : 'y'} selected
                    </Typography>
                </Box>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ width: '40px', pl: 0.5 }}>
                                <Checkbox
                                    indeterminate={selectedCategories.length > 0 && !isAllSelected}
                                    checked={isAllSelected}
                                    onChange={handleSelectAllCategories}
                                    className='table-checkboxes'
                                    size='small'
                                />
                            </TableCell>
                            <TableCell>Category Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Skills Count</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories === null ? (
                            null // Don't show anything while loading
                        ) : categories.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14): a plain line. */}
                                    <EmptyState framed={false} title="No categories found" message={null} />
                                </TableCell>
                            </TableRow>
                        ) : (
                            categories.map((category) => (
                                <TableRow key={category.id}>
                                    <TableCell padding="checkbox" sx={{ width: '40px', pl: 0.5 }}>
                                        <Checkbox
                                            checked={selectedCategories.includes(category.id)}
                                            onChange={() => handleSelectCategory(category.id)}
                                            className='table-checkboxes'
                                            size='small'
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {/* <Typography variant="body2" fontWeight="medium">
                                            {category.category_name}
                                        </Typography> */}
                                        {permissions.canEdit ? (
                                            <Box
                                                sx={{
                                                    cursor: "pointer",
                                                    color: "primary.main",
                                                }}
                                                onClick={() => handleEditCategory(category)}
                                            >
                                                {category.category_name}
                                            </Box>
                                        ) : (
                                            <Box>{category.category_name}</Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {category.description || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={`${category.skills_count || 0} skills`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(category.created_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            {permissions.canEdit && (
                                                <Tooltip title="Edit Category">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditCategory(category)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCategories}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* Modals */}
            <CategoryFormModal
                open={categoryFormModalOpen}
                onClose={() => setCategoryFormModalOpen(false)}
                category={selectedCategory}
                onSuccess={handleCategoryFormSuccess}
            />

            <BulkDeleteModal
                open={bulkDeleteModalOpen}
                onClose={() => setBulkDeleteModalOpen(false)}
                ids={selectedCategories}
                onSuccess={() => {
                    showToast('Categories deleted successfully', 'success');
                    setBulkDeleteModalOpen(false);
                    fetchCategories();
                    setSelectedCategories([]);
                }}
                deleteFunction={skillService.bulkDeleteCategories}
                title="Delete Categories"
                itemName="category"
            />
        </Box>
    );
};

export default CategoriesList;